import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/services/api";
import type { CreateSessionRequest } from "@/types";
import { createCrudHooks } from "@/hooks/crudHooksFactory";

export type SessionListScope = "mine" | "all";

export function useSessions(scope: SessionListScope = "mine") {
  return useQuery({
    queryKey: ["sessions", scope],
    queryFn: () => apiClient.getSessions({ scope }),
  });
}

export function useSession(id: string) {
  return useQuery({
    queryKey: ["sessions", id],
    queryFn: () => apiClient.getSession(id),
    enabled: !!id,
  });
}

export function useVehicleSessions(vehicleId: string, limit = 3) {
  return useQuery({
    queryKey: ["sessions", "vehicle", { vehicleId, limit }],
    queryFn: () =>
      apiClient.getSessions({
        vehicleId,
        limit,
        orderBy: "conductedAt",
        orderDirection: "desc",
      }),
    enabled: !!vehicleId,
  });
}

const sessionCrudHooks = createCrudHooks({
  create: {
    mutationFn: (sessionData: CreateSessionRequest) =>
      apiClient.createSession(sessionData),
    cacheActions: [
      { type: "invalidate", key: ["sessions"] },
      { type: "invalidate", key: ["dashboard"] },
    ],
    successMessageKey: "pages:sessions.toast.createSuccess",
    errorMessageKey: "pages:sessions.toast.createFailed",
  },
  update: {
    mutationFn: ({
      id,
      ...sessionData
    }: { id: string } & Partial<CreateSessionRequest>) =>
      apiClient.updateSession(id, sessionData),
    cacheActions: [
      { type: "invalidate", key: ["sessions"] },
      {
        type: "invalidate",
        key: (updatedSession) => ["sessions", updatedSession.id],
      },
      { type: "invalidate", key: ["dashboard"] },
    ],
    successMessageKey: "pages:sessions.toast.updateSuccess",
    errorMessageKey: "pages:sessions.toast.updateFailed",
  },
  delete: {
    mutationFn: (id: string) => apiClient.deleteSession(id),
    cacheActions: [
      { type: "invalidate", key: ["sessions"] },
      { type: "invalidate", key: ["dashboard"] },
    ],
    successMessageKey: "pages:sessions.toast.deleteSuccess",
    errorMessageKey: "pages:sessions.toast.deleteFailed",
  },
});

export const useCreateSession = sessionCrudHooks.useCreate;
export const useUpdateSession = sessionCrudHooks.useUpdate;
export const useDeleteSession = sessionCrudHooks.useDelete;
